/**
 * Rich HTML Serializer for TipTap/ProseMirror documents.
 *
 * Converts a TipTap document (or selection fragment) to self-contained
 * HTML with inline styles. The output is understood by Word, Google Docs,
 * and other rich-text applications when placed on the clipboard.
 *
 * Accepts an optional style config parameter (defaults to transcriptStyles)
 * so that future style customization or .docx export can pass overrides.
 */

import transcriptStyles from '../config/transcriptStyles';

/**
 * Convert a camelCase style object to an inline CSS string.
 * { fontWeight: 'bold', color: '#888' } => "font-weight:bold;color:#888"
 */
function styleObjectToString(styleObj) {
  return Object.entries(styleObj)
    .map(([key, value]) => {
      const kebab = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      return `${kebab}:${value}`;
    })
    .join(';');
}

/**
 * Merge multiple style objects into one.
 * Later entries override earlier ones for the same property.
 */
function mergeStyles(...objs) {
  return Object.assign({}, ...objs);
}

/**
 * Escape HTML special characters in text content.
 */
function escapeHTML(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Preserve consecutive spaces by converting them to alternating
 * &nbsp; and regular space characters. This prevents Word, Google Docs,
 * and other rich-text applications from collapsing whitespace runs,
 * since &nbsp; is never collapsed.
 *
 * For example:
 *   "    " (4 spaces) => "&nbsp; &nbsp; "
 *   "   "  (3 spaces) => "&nbsp; &nbsp;"
 *
 * Single spaces are left as-is (they don't get collapsed).
 */
function preserveSpaces(str) {
  return str.replace(/ {2,}/g, (match) => {
    let result = '';
    for (let i = 0; i < match.length; i++) {
      result += i % 2 === 0 ? '\u00a0' : ' ';
    }
    return result;
  });
}

/**
 * Serialize the inline content of a paragraph node to HTML with
 * inline styles for any TipTap marks (bold, italic, underline, etc.).
 */
function serializeInlineContent(node, config) {
  let html = '';

  node.forEach((child) => {
    if (child.isText) {
      let text = preserveSpaces(escapeHTML(child.text));

      if (child.marks && child.marks.length > 0) {
        const openTags = [];
        const closeTags = [];

        child.marks.forEach((mark) => {
          const markStyle = config.marks[mark.type.name];
          if (markStyle && Object.keys(markStyle).length > 0) {
            const styleStr = styleObjectToString(markStyle);
            openTags.push(`<span style="${styleStr}">`);
            closeTags.unshift('</span>');
          }
        });

        text = openTags.join('') + text + closeTags.join('');
      }

      html += text;
    } else if (child.type.name === 'hardBreak') {
      html += '<br>';
    }
  });

  return html;
}

/**
 * Serialize a TipTap/ProseMirror document or fragment to rich HTML
 * with inline styles.
 *
 * Works with both a full document Node and a selection Fragment,
 * since both support the .forEach() iteration API.
 *
 * @param {Node|Fragment} docOrFragment - ProseMirror document or fragment
 * @param {Object} [config] - Style config (defaults to transcriptStyles)
 * @returns {string} Self-contained HTML string
 */
export default function serializeToRichHTML(docOrFragment, config = transcriptStyles) {
  const paragraphs = [];

  docOrFragment.forEach((node) => {
    if (node.type.name === 'paragraph') {
      const lineType = node.attrs.lineType || 'default';

      // Merge base styles with line-type-specific styles
      const lineTypeStyle = config.lineTypes[lineType] || {};
      const pStyle = mergeStyles(config.base, lineTypeStyle);
      const styleStr = styleObjectToString(pStyle);

      // Serialize the inline children (text, marks, hard breaks)
      const innerHTML = node.childCount > 0
        ? serializeInlineContent(node, config)
        : '<br>';  // empty paragraphs need <br> to preserve spacing

      paragraphs.push(`<p style="${styleStr}">${innerHTML}</p>`);
    }
  });

  return paragraphs.join('\n');
}
